import {ModelSetOptions} from 'backbone';

import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import {SequenceLinearMapState} from 'molbio/maps/MapStates';
import OligoLinearMapTabState from 'molbio/tabstates/OligoLinearMapTabState';
import RNASequenceLinearMapTabState from 'molbio/tabstates/RNASequenceLinearMapTabState';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

// This is silly but all of these classes are defined separately even thought here are really minimal differences.
// At a minimum, we should have a consolidated base class, but even better would be to just have one class that
// dynamically sets up its attributes in `initialize()`.
export type GenericLinearMapTabState =
  | SequenceLinearMapTabState
  | RNASequenceLinearMapTabState
  | OligoLinearMapTabState;

export interface SequenceLinearMapTabStateAttributes extends SinglePolymerMapTabStateAttributes<
  Sequence,
  SequenceLinearMapState
> {
  visibilityOptions: VisibilityOptions;
  bufferViewLayout: BufferViewLayout | null;
}

interface SequenceLinearMapTabState {
  get<K extends keyof SequenceLinearMapTabStateAttributes>(key: K): SequenceLinearMapTabStateAttributes[K];

  set<K extends keyof SequenceLinearMapTabStateAttributes>(
    attributeName: K,
    value: SequenceLinearMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): SequenceLinearMapTabState;

  set(obj: Partial<SequenceLinearMapTabStateAttributes>): SequenceLinearMapTabState;
}

class SequenceLinearMapTabState extends SinglePolymerMapTabState<Sequence, SequenceLinearMapState> {
  mapState(options: {polymer: Sequence; visibilityOptions: VisibilityOptions}): SequenceLinearMapState {
    return new SequenceLinearMapState(options);
  }

  getVisibilityOptions(): VisibilityOptions {
    return this.get('visibilityOptions');
  }

  msVisibilityOptions(): VisibilityOptions {
    const transcriptionLinksPresent = this.sequence()._sequence().tagSchemaHasTranscriptionLinkFields();
    const visibilityOptions = this.get('visibilityOptions');
    if (!transcriptionLinksPresent) {
      visibilityOptions.remove(visibilityOptions.where({name: vo.TRANSCRIPTIONS.name}));
    }
    return visibilityOptions.makeLocalSync();
  }

  override defaults(): TabStateDefaults & Partial<SequenceLinearMapTabStateAttributes> {
    return {
      ...super.defaults(),
      closeable: false,
      title: 'Sequence Map',
      bufferViewLayout: null,
      visibilityOptions: constructOptions(
        'linear',
        // @ts-expect-error: ts does not recognize that filter logic removes falsey values
        [
          {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
          {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
          BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS && {
            base: vo.PART_ANNOTATIONS,
            visible: true,
            editable: true,
            sync: true,
          },
          {base: vo.PRIMERS, visible: true, editable: true, sync: true},
          {base: vo.PRIMER_BASES, visible: true, editable: true, sync: true},
          {base: vo.CUTSITES, visible: true, editable: true, sync: true},
          {base: vo.COMPLEMENT, visible: true, editable: true, sync: true},
          {base: vo.ORFS, visible: false, editable: true, sync: true},
          {base: vo.SEQUENCE_AXIS, visible: true, editable: true, sync: true},
          {base: vo.HOMOLOGY_REGION_AXIS, visible: false, editable: false, sync: false},
          {base: vo.TRANSLATIONS, visible: true, editable: true, sync: true},
          {
            base: vo.TRANSCRIPTIONS,
            visible: true,
            editable: true,
            sync: true,
          },
          {base: vo.AA_INDICES, visible: true, editable: true, sync: true},
          // TODO(Rachel): Remove.
          {base: vo.OVERHANGS, visible: false, editable: true, sync: true},
          {base: vo.DNA, visible: true, editable: false, sync: true},
          {base: vo.AMINO_ACIDS, visible: true, editable: false, sync: true},
          {base: vo.SEARCH_BASES, visible: true, editable: false, sync: true},
          {base: vo.PARTS, visible: true, editable: false},
        ].filter((option) => option)
      ),
      preferenceKey: 'sequence-linear-map',
    };
  }
}

export default SequenceLinearMapTabState;
